import { useCanEditAuditRoles } from "PFApp/activities/hooks/use_can_edit_audit_roles";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { AddActivityProfilesButton } from "PFApp/activities/show/parts/activity_profiles/add_activity_profiles_button";
import { useActivityTabsStore } from "PFApp/activities/show/store/activity_tabs";
import { ActivityTab } from "PFApp/activities/types/activity_tab";
import { useCanViewActivityInBM } from "PFApp/booking/hooks/use_can_view_activity_in_bm";
import { ShareButton } from "PFApp/components/activity_share/share_button";
import ViewInBookingModuleButton from "PFApp/components/view_in_booking_module_button";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import { Icon } from "PFComponents/icon";
import { Tooltip } from "PFComponents/tooltip";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { ManageTagsButton } from "../../tag/manage_tags_button";
import css from "./activity_actions.module.scss";
import { ActivityShowMenuButton } from "./activity_details_menu_button";

export const ActivityActions = () => {
  const { t } = useTranslation("activities");
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activity = useCurrentActivity();
  const { activityId, isAudit, isRole } = useActivityShowContext();
  const { shortlistsMeta, suspendedShortlistsMeta } = useActivityShortlists();

  const canSeeViewInBookingModuleButton = useCanViewActivityInBM({ activity });
  const tabActiveId = useActivityTabsStore((state) => state.tabActiveId);

  const { canEditAuditRole } = useCanEditAuditRoles({
    coowned: activity.coowned,
    templateId: activity.template_id,
    workflowState: activity.workflow_state
  });
  const template = useTemplate(activity.template_id);

  if (!activity) {
    return null;
  }

  const shortListCount = (shortlistsMeta?.total || 0) + (suspendedShortlistsMeta?.total || 0);

  const isAuditRole = isAudit && isRole;

  const isEditRegularRolesEnabled = !isAuditRole && activity.coowned;
  const isEditEnabled = isEditRegularRolesEnabled || canEditAuditRole;

  const originId = searchParams.get("origin_id");
  const originType = searchParams.get("origin_type");
  const showBackButton = originId && originType && !activity.coowned;
  const originTypeLabel =
    originType && t(`templates.${originType as TemplateKey}`, { defaultValue: originType });

  return (
    <div className={css.actions}>
      {showBackButton && (
        <ButtonLink
          kind="secondary"
          href={`/activities/${originId}`}
          state={{ utmSource: location?.state?.utmSource }}
        >
          <>
            <Icon name="arrow-left" size="sm" />
            {t("show.parts.backTo", { location: originTypeLabel })}
          </>
        </ButtonLink>
      )}
      {isEditEnabled && (
        <Tooltip content={String(t((isRole ? "show.parts.editRole" : "show.parts.editEngagement") as any))}>
          <Button
            className={css.editLink}
            onClick={() => navigate(`/activities/${activity.id}/edit`)}
            kind="secondary"
          >
            <Icon name="edit" size="sm" />
          </Button>
        </Tooltip>
      )}
      <AddActivityProfilesButton />
      <ManageTagsButton activity={activity} />
      {canSeeViewInBookingModuleButton && (
        <ViewInBookingModuleButton
          activityId={activityId}
          openAllocateManually={tabActiveId === ActivityTab.Shortlist && shortListCount > 0}
          className={css.viewInBMButton}
        />
      )}
      {activity.state !== "draft" && activity.coowned && !isAudit && (
        <Tooltip content={String(t((isRole ? "show.parts.shareRole" : "show.parts.shareEngagement") as any))}>
          <ShareButton
            templateKey={template?.key}
            kind="secondary"
            activityIds={[activity.id]}
            withText={false}
          />
        </Tooltip>
      )}
      <ActivityShowMenuButton />
    </div>
  );
};
